import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useLanguage } from '../../lib/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Plus, Pause, Play, X, Loader2, Repeat, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function VirementsProgrammes() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [form, setForm] = useState({
    beneficiary_id: '',
    beneficiaire_nom: '',
    beneficiaire_iban: '',
    beneficiaire_bic: '',
    montant: '',
    frequency: 'monthly',
    start_date: '',
    end_date: '',
    motif: '',
  });

  const frequencyLabels = {
    once: t('scheduledTransfers.once'),
    weekly: t('scheduledTransfers.weekly'),
    biweekly: t('scheduledTransfers.biweekly'),
    monthly: t('scheduledTransfers.monthly'),
    quarterly: t('scheduledTransfers.quarterly'),
    yearly: t('scheduledTransfers.yearly'),
  };

  const statusBadge = (s) => {
    const map = {
      active: { v: 'default', l: t('scheduledTransfers.active'), className: 'bg-emerald-600 hover:bg-emerald-700' },
      paused: { v: 'secondary', l: t('scheduledTransfers.paused'), className: '' },
      cancelled: { v: 'destructive', l: t('scheduledTransfers.cancelled'), className: '' },
      completed: { v: 'secondary', l: t('scheduledTransfers.completed'), className: '' },
      pending: { v: 'secondary', l: t('scheduledTransfers.pending'), className: '' },
    };
    const x = map[s] || { v: 'secondary', l: s, className: '' };
    return <Badge variant={x.v} className={x.className}>{x.l}</Badge>;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/client/scheduled-transfers');
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(t('scheduledTransfers.loadError'));
    } finally {
      setLoading(false);
    }
  };
  const loadBeneficiaries = async () => {
    try {
      const { data } = await api.get('/client/beneficiaries');
      setBeneficiaries(Array.isArray(data) ? data : (data?.items || []));
    } catch { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadBeneficiaries(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickBeneficiary = (id) => {
    const b = beneficiaries.find((x) => x.id === id);
    if (!b) {
      upd('beneficiary_id', '');
      return;
    }
    setForm((f) => ({
      ...f,
      beneficiary_id: id,
      beneficiaire_nom: b.name || '',
      beneficiaire_iban: b.iban || '',
      beneficiaire_bic: b.bic || '',
    }));
  };

  const submit = async () => {
    if (!form.beneficiaire_nom || !form.beneficiaire_iban || !form.montant || !form.start_date) {
      toast.error(t('scheduledTransfers.required'));
      return;
    }
    const amount = parseFloat(form.montant);
    if (!amount || amount <= 0) { toast.error(t('scheduledTransfers.invalidAmount')); return; }
    setSaving(true);
    try {
      const payload = {
        beneficiary_id: form.beneficiary_id || null,
        beneficiaire_nom: form.beneficiaire_nom.trim(),
        beneficiaire_iban: form.beneficiaire_iban.replace(/\s+/g, '').toUpperCase(),
        beneficiaire_bic: (form.beneficiaire_bic || '').replace(/\s+/g, '').toUpperCase() || null,
        montant: amount,
        frequency: form.frequency,
        start_date: form.start_date,
        end_date: form.end_date || null,
        motif: form.motif || '',
      };
      await api.post('/client/scheduled-transfers', payload);
      toast.success(t('scheduledTransfers.created'));
      setShowAdd(false);
      setForm({ beneficiary_id: '', beneficiaire_nom: '', beneficiaire_iban: '', beneficiaire_bic: '', montant: '', frequency: 'monthly', start_date: '', end_date: '', motif: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('scheduledTransfers.createError'));
    } finally {
      setSaving(false);
    }
  };

  const action = async (id, type) => {
    try {
      await api.patch(`/client/scheduled-transfers/${id}/${type}`);
      toast.success(type === 'pause' ? t('scheduledTransfers.paused2') : type === 'resume' ? t('scheduledTransfers.resumed') : t('scheduledTransfers.cancelled2'));
      load();
    } catch (e) {
      // fallback : DELETE endpoint for cancel
      if (type === 'cancel') {
        try {
          await api.delete(`/client/scheduled-transfers/${id}`);
          toast.success(t('scheduledTransfers.cancelled2'));
          load();
          return;
        } catch { /* noop */ }
      }
      toast.error(e?.response?.data?.detail || t('scheduledTransfers.error'));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Calendar className="w-6 h-6 text-blue-700"/> {t('scheduledTransfers.title')}</h1>
          <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('scheduledTransfers.subtitle')}</p>
        </div>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setShowAdd(true)} data-testid="btn-add-scheduled">
          <Plus className="w-4 h-4 mr-1"/> {t('scheduledTransfers.addScheduled')}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden dark:bg-slate-900 dark:border-slate-700">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Repeat className="w-7 h-7"/></div>
            <h2 className="font-semibold">{t('scheduledTransfers.noScheduled')}</h2>
            <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">{t('scheduledTransfers.noScheduledHint')}</p>
            <Button className="mt-4 bg-blue-700" onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-1"/> {t('scheduledTransfers.createScheduled')}</Button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {items.map((it) => (
              <li key={it.id} className="p-4 flex items-center gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><Repeat className="w-5 h-5"/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex flex-wrap items-center gap-2">
                    {it.beneficiaire_nom}
                    {statusBadge(it.status)}
                    <span className="text-xs bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-2 py-0.5 rounded-full">{frequencyLabels[it.frequency] || it.frequency}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-0.5">{it.beneficiaire_iban}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {it.start_date && <>{t('scheduledTransfers.start')} : {new Date(it.start_date).toLocaleDateString()}</>}
                    {it.end_date && <> • {t('scheduledTransfers.end')} : {new Date(it.end_date).toLocaleDateString()}</>}
                    {it.next_execution_date && <> • {t('scheduledTransfers.next')} : {new Date(it.next_execution_date).toLocaleDateString()}</>}
                  </div>
                  {it.motif && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('scheduledTransfers.motif')} : {it.motif}</div>}
                </div>
                <div className="text-right">
                  <div className="font-bold">{(it.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
                </div>
                <div className="flex gap-2">
                  {it.status === 'active' && (
                    <Button size="sm" variant="outline" onClick={() => action(it.id, 'pause')} data-testid={`btn-pause-${it.id}`}>
                      <Pause className="w-3.5 h-3.5 mr-1"/> {t('scheduledTransfers.pause')}
                    </Button>
                  )}
                  {it.status === 'paused' && (
                    <Button size="sm" className="bg-blue-700" onClick={() => action(it.id, 'resume')} data-testid={`btn-resume-${it.id}`}>
                      <Play className="w-3.5 h-3.5 mr-1"/> {t('scheduledTransfers.resume')}
                    </Button>
                  )}
                  {(it.status === 'active' || it.status === 'paused') && (
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => action(it.id, 'cancel')} data-testid={`btn-cancel-${it.id}`}>
                      <X className="w-4 h-4"/>
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('scheduledTransfers.scheduleTransfer')}</DialogTitle>
            <DialogDescription>{t('scheduledTransfers.dialogDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {beneficiaries.length > 0 && (
              <div>
                <Label>{t('scheduledTransfers.savedBeneficiary')}</Label>
                <Select value={form.beneficiary_id || 'none'} onValueChange={(v) => pickBeneficiary(v === 'none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder={t('scheduledTransfers.pickBeneficiary')}/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('scheduledTransfers.manualEntry')}</SelectItem>
                    {beneficiaries.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.label} ({b.name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('scheduledTransfers.beneficiaryName')}</Label><Input value={form.beneficiaire_nom} onChange={(e) => upd('beneficiaire_nom', e.target.value)} data-testid="input-sched-name"/></div>
              <div><Label>{t('scheduledTransfers.amountEuro')}</Label><Input type="number" step="0.01" value={form.montant} onChange={(e) => upd('montant', e.target.value)} data-testid="input-sched-amount"/></div>
            </div>
            <div><Label>{t('scheduledTransfers.iban')}</Label><Input value={form.beneficiaire_iban} onChange={(e) => upd('beneficiaire_iban', e.target.value.toUpperCase())} className="font-mono" data-testid="input-sched-iban"/></div>
            <div><Label>{t('scheduledTransfers.bicOptional')}</Label><Input value={form.beneficiaire_bic} onChange={(e) => upd('beneficiaire_bic', e.target.value.toUpperCase())} data-testid="input-sched-bic"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('scheduledTransfers.periodicity')}</Label>
                <Select value={form.frequency} onValueChange={(v) => upd('frequency', v)}>
                  <SelectTrigger data-testid="select-frequency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">{t('scheduledTransfers.once')}</SelectItem>
                    <SelectItem value="weekly">{t('scheduledTransfers.weekly')}</SelectItem>
                    <SelectItem value="biweekly">{t('scheduledTransfers.biweekly')}</SelectItem>
                    <SelectItem value="monthly">{t('scheduledTransfers.monthly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('scheduledTransfers.startDateLabel')}</Label><Input type="date" value={form.start_date} onChange={(e) => upd('start_date', e.target.value)} data-testid="input-start-date"/></div>
            </div>
            <div><Label>{t('scheduledTransfers.endDateOptional')}</Label><Input type="date" value={form.end_date} onChange={(e) => upd('end_date', e.target.value)} data-testid="input-end-date"/></div>
            <div><Label>{t('scheduledTransfers.motifLabel')}</Label><Textarea rows={2} value={form.motif} onChange={(e) => upd('motif', e.target.value)} data-testid="input-sched-motif"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t('scheduledTransfers.cancel')}</Button>
            <Button className="bg-blue-700" onClick={submit} disabled={saving} data-testid="btn-save-scheduled">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <ChevronRight className="w-4 h-4 mr-1"/>} {t('scheduledTransfers.schedule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
